import type { EditorTextBoxConversion } from './tinymce-conversion'
import { editorTextBoxConversionReportCollector } from './tinymce-conversion/report'

type ConvertedRun =
  EditorTextBoxConversion['box']['blocks'][number]['runs'][number]

/**
 * The ids of the service rows a case holds, from the editor's case record,
 * whose `services` rows carry them as `id`. A case with no services holds
 * none.
 */
export const editorCaseServiceIds = (
  caseDetails: unknown,
): ReadonlySet<string> => {
  const services = (caseDetails as { services?: unknown } | null | undefined)
    ?.services
  if (!Array.isArray(services)) return new Set()
  return new Set(
    services.flatMap((row: unknown) => {
      const id = (row as { id?: unknown } | null)?.id
      return typeof id === 'string' && id !== '' ? [id] : []
    }),
  )
}

const withoutServiceBinding = (
  run: Extract<ConvertedRun, { kind: 'field' }>,
): ConvertedRun => {
  const { caseServiceId: _caseServiceId, params, ...unbound } = run
  const { serviceId: _serviceId, ...otherParams } = params ?? {}
  return Object.keys(otherParams).length === 0
    ? unbound
    : { ...unbound, params: otherParams }
}

/**
 * The conversion with every service binding the case does not hold taken
 * off. A converted service block keeps the service row TinyMCE stored for
 * it; the save route refuses the whole box when that row is gone, and a row
 * of another case prints nothing, so a run bound to a row outside the case
 * is written unbound, the way a `service` template converts, and shows the
 * case's default service. The report counts the runs this unbound.
 */
export const editorTextBoxConversionForCaseServices = (
  conversion: EditorTextBoxConversion,
  caseServiceIds: ReadonlySet<string>,
): EditorTextBoxConversion => {
  let unboundRuns = 0
  const blocks = conversion.box.blocks.map((block) => ({
    ...block,
    runs: block.runs.map((run): ConvertedRun => {
      if (
        run.kind !== 'field' ||
        run.caseServiceId === undefined ||
        caseServiceIds.has(run.caseServiceId)
      )
        return run
      unboundRuns += 1
      return withoutServiceBinding(run)
    }),
  }))
  if (unboundRuns === 0) return conversion
  const report = editorTextBoxConversionReportCollector()
  for (const entry of conversion.report) report.add(entry)
  report.add({ kind: 'service-binding-dropped', count: unboundRuns })
  return { box: { ...conversion.box, blocks }, report: report.entries() }
}
